import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from 'services/api/api';
import { finalizeMonitoringSession } from 'services/monitoring';

declare global {
  interface Window {
    __isAuth?: boolean;
    __preferencesLoaded?: boolean;
    __mapPreferencesApplied?: boolean;
  }
}

type AuthUser = {
  id: number;
  name: string;
  email: string;
  provider: 'google' | 'govbr' | null;
  picture: string;
  session_expires_at: string;
  is_staff: boolean;
};

type AuthContextValue = {
  user: AuthUser | null;
  isAdmin: boolean;
  loading: boolean;
  preferencesLoaded: boolean;
  sessionExpired: boolean;
  acknowledgeSessionExpired: () => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const preferenceKeys = ['layersFavorite', 'ocultGraph', 'favoriteGraphics', 'favoriteCards', 'hiddenGraphics', 'hiddenCards', 'searchResultLayers', 'basemap', 'activeCategories', 'activeLayers', 'mapView'];

const dontAutoSaveKeys = ['mapView'];

const getUserPreference = (key: string) => sessionStorage.getItem(key);
const setUserPreference = (key: string, value: string) => sessionStorage.setItem(key, value);
const removeUserPreference = (key: string) => sessionStorage.removeItem(key);
const clearUserPreferences = () => preferenceKeys.forEach(removeUserPreference);

const originalGetItem = localStorage.getItem;
const originalSetItem = localStorage.setItem;
const originalRemoveItem = localStorage.removeItem;

export const clearStoredPreferences = () => {
  preferenceKeys.forEach(key => {
    try {
      originalRemoveItem.call(localStorage, key);
    } catch {}
    try {
      sessionStorage.removeItem(key);
    } catch {}
    try {
      sessionStorage.removeItem('p3m_anon_' + key);
    } catch {}
  });
  try {
    sessionStorage.removeItem('pesquisas');
    sessionStorage.removeItem('p3m_anonymous_session');
  } catch {}
};

export const restoreAnonymousPreferencesFromSession = () => {
  if (window.__isAuth) return;
  if (!sessionStorage.getItem('p3m_anonymous_session')) return;

  preferenceKeys.forEach(key => {
    try {
      const val = sessionStorage.getItem('p3m_anon_' + key);
      if (val !== null && originalGetItem.call(localStorage, key) === null) {
        originalSetItem.call(localStorage, key, val);
      }
    } catch {}
  });
};

if (!window.__isAuth) {
  if (!sessionStorage.getItem('p3m_anonymous_session')) {
    clearStoredPreferences();
  } else {
    restoreAnonymousPreferencesFromSession();
  }
}

localStorage.getItem = function (key) {
  if (window.__isAuth && preferenceKeys.includes(key)) {
    return getUserPreference(key);
  }
  return originalGetItem.call(this, key);
};

localStorage.setItem = function (key, value) {
  if (window.__isAuth && preferenceKeys.includes(key)) {
    if (getUserPreference(key) === value) return;
    setUserPreference(key, value);
    if (!dontAutoSaveKeys.includes(key)) {
      window.dispatchEvent(new CustomEvent('preferencesUpdated'));
    }
    return;
  }
  originalSetItem.call(this, key, value);
  if (preferenceKeys.includes(key)) {
    try {
      sessionStorage.setItem('p3m_anon_' + key, value);
      sessionStorage.setItem('p3m_anonymous_session', 'active');
    } catch {}
  }
};

localStorage.removeItem = function (key) {
  if (window.__isAuth && preferenceKeys.includes(key)) {
    if (getUserPreference(key) === null) return;
    removeUserPreference(key);
    if (!dontAutoSaveKeys.includes(key)) {
      window.dispatchEvent(new CustomEvent('preferencesUpdated'));
    }
    return;
  }
  originalRemoveItem.call(this, key);
  if (preferenceKeys.includes(key)) {
    try {
      sessionStorage.removeItem('p3m_anon_' + key);
    } catch {}
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const clearAuthState = useCallback(() => {
    setUser(null);
    window.__isAuth = false;
    window.__preferencesLoaded = false;
    window.__mapPreferencesApplied = false;
    setPreferencesLoaded(false);
    clearStoredPreferences();
  }, []);

  useEffect(() => {
    api.get<AuthUser>('/auth/me/')
      .then(async response => {
        setUser(response.data);
        window.__isAuth = true;
        window.__preferencesLoaded = false;
        window.__mapPreferencesApplied = false;
        setPreferencesLoaded(false);
        clearUserPreferences();

        preferenceKeys.forEach(key => originalRemoveItem.call(localStorage, key));

        try {
          const prefRes = await api.get('/auth/preferences/');
          const prefs = prefRes.data;

          if (prefs.favorite_layers && Object.keys(prefs.favorite_layers).length > 0) {
            setUserPreference('layersFavorite', JSON.stringify(prefs.favorite_layers));
          }
          if (prefs.hidden_charts && Object.keys(prefs.hidden_charts).length > 0) {
            if (prefs.hidden_charts.ocultGraph) setUserPreference('ocultGraph', JSON.stringify(prefs.hidden_charts.ocultGraph));
            if (prefs.hidden_charts.hiddenGraphics) setUserPreference('hiddenGraphics', JSON.stringify(prefs.hidden_charts.hiddenGraphics));
            if (prefs.hidden_charts.hiddenCards) setUserPreference('hiddenCards', JSON.stringify(prefs.hidden_charts.hiddenCards));
          }
          if (prefs.favorite_charts && Object.keys(prefs.favorite_charts).length > 0) {
            if (prefs.favorite_charts.favoriteGraphics) setUserPreference('favoriteGraphics', JSON.stringify(prefs.favorite_charts.favoriteGraphics));
            if (prefs.favorite_charts.favoriteCards) setUserPreference('favoriteCards', JSON.stringify(prefs.favorite_charts.favoriteCards));
          }
          if (prefs.searchResultLayers) setUserPreference('searchResultLayers', JSON.stringify(prefs.searchResultLayers));
          if (prefs.basemap) setUserPreference('basemap', JSON.stringify(prefs.basemap));
          if (prefs.activeCategories) setUserPreference('activeCategories', JSON.stringify(prefs.activeCategories));
          if (prefs.activeLayers) {
            setUserPreference('activeLayers', JSON.stringify(prefs.activeLayers));
          }
          if (prefs.mapView) setUserPreference('mapView', JSON.stringify(prefs.mapView));
          window.__preferencesLoaded = true;
          setPreferencesLoaded(true);
          window.dispatchEvent(new CustomEvent('preferencesLoaded'));
        } catch (err) {
          window.__preferencesLoaded = true;
          setPreferencesLoaded(true);
          window.dispatchEvent(new CustomEvent('preferencesLoaded'));
          console.error("Failed to load preferences", err);
        }
      })
      .catch(() => {
        setUser(null);
        window.__isAuth = false;
        window.__preferencesLoaded = false;
        window.__mapPreferencesApplied = false;
        setPreferencesLoaded(true);
        clearUserPreferences();
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;

    const expireAt = new Date(user.session_expires_at).getTime();
    const timeUntilExpiration = expireAt - Date.now();

    if (!Number.isFinite(expireAt) || timeUntilExpiration <= 0) {
      setSessionExpired(true);
      return;
    }

    const timeout = setTimeout(() => setSessionExpired(true), timeUntilExpiration);
    return () => clearTimeout(timeout);
  }, [user]);

  useEffect(() => {
    const handleSessionExpired = () => {
      if (user) setSessionExpired(true);
    };

    window.addEventListener('p3m:sessionExpired', handleSessionExpired);
    return () => window.removeEventListener('p3m:sessionExpired', handleSessionExpired);
  }, [user]);

  useEffect(() => {
    if (!user) return;

    let syncTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastPayload = '';

    const handlePreferencesUpdate = () => {
      if (syncTimeout) clearTimeout(syncTimeout);

      syncTimeout = setTimeout(() => {
        const getParsed = (key: string, def: any) => {
          try { return JSON.parse(getUserPreference(key) || JSON.stringify(def)); } catch { return def; }
        };

        const payload = {
          favorite_layers: getParsed('layersFavorite', {}),
          hidden_charts: {
            ocultGraph: getParsed('ocultGraph', {}),
            hiddenGraphics: getParsed('hiddenGraphics', []),
            hiddenCards: getParsed('hiddenCards', [])
          },
          favorite_charts: {
            favoriteGraphics: getParsed('favoriteGraphics', []),
            favoriteCards: getParsed('favoriteCards', [])
          },
          searchResultLayers: getParsed('searchResultLayers', []),
          basemap: getParsed('basemap', {}),
          activeCategories: getParsed('activeCategories', {}),
          activeLayers: getParsed('activeLayers', []),
          mapView: getParsed('mapView', {})
        };

        const serializedPayload = JSON.stringify(payload);
        if (serializedPayload === lastPayload) return;

        lastPayload = serializedPayload;
        api.post('/auth/preferences/', payload).catch(err => console.error("Sync pref error:", err));
      }, 300);
    };

    window.addEventListener('preferencesUpdated', handlePreferencesUpdate);
    return () => {
      if (syncTimeout) clearTimeout(syncTimeout);
      window.removeEventListener('preferencesUpdated', handlePreferencesUpdate);
    };
  }, [user]);

  const value = useMemo(() => ({
    user,
    isAdmin: Boolean(user?.is_staff),
    loading,
    preferencesLoaded,
    sessionExpired,
    acknowledgeSessionExpired: () => {
      setSessionExpired(false);
      clearAuthState();
      window.dispatchEvent(new CustomEvent('p3m:clearMapDisplay'));
    },
    logout: async () => {
      window.dispatchEvent(new CustomEvent('p3m:saveMapPreferences'));
      await new Promise(resolve => setTimeout(resolve, 400));
      await finalizeMonitoringSession({ useBeacon: true });
      await api.post('/auth/logout/');
      clearAuthState();
      window.dispatchEvent(new CustomEvent('p3m:clearMapDisplay'));
    },
  }), [user, loading, preferencesLoaded, sessionExpired, clearAuthState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return context;
}

